import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';

/**
 * Datadog browser adapter — RUM (error tracking, Session Replay, performance)
 * plus browser log collection.
 *
 * Everything here is gated on the `NEXT_PUBLIC_DD_*` env vars and NO-OPS when
 * they are unset, so this is safe to deploy before the RUM application creds
 * land in Vercel. Every capture helper is guarded on `isInitialized`, so
 * calling one before/without init is a harmless no-op on both server and
 * client.
 *
 * This module is consumed by the unified telemetry seam in `./telemetry`; call
 * sites should import from there rather than reaching for these `dd*` helpers
 * directly.
 */

// RUM application credentials (from the Datadog RUM application).
const applicationId = process.env.NEXT_PUBLIC_DD_RUM_APPLICATION_ID;
const clientToken = process.env.NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN;
const site = process.env.NEXT_PUBLIC_DD_SITE;

// RUM `env` tag. Falls back to `NEXT_PUBLIC_TARGET_ENV` so RUM env tagging is
// never empty.
const ddEnv = process.env.NEXT_PUBLIC_DD_ENV || process.env.NEXT_PUBLIC_TARGET_ENV;

// Release version tag (the git commit SHA on Vercel). `next.config.js` inlines
// this into the client bundle via its `env` key and feeds the SAME value to the
// Datadog source-map upload plugin as `releaseVersion`. The two MUST stay equal
// or RUM errors won't deobfuscate against the uploaded maps. Undefined locally.
const ddVersion = process.env.NEXT_PUBLIC_DD_VERSION;

// Unified service tagging — must match backend `DD_SERVICE` so frontend RUM
// sessions correlate with backend APM / LLM Obs traces. Must ALSO match the
// `service` passed to the source-map plugin in `next.config.js`.
const DD_SERVICE = 'medscout-frontend';

let isInitialized = false;

/** Capture context (`{ tags, extra }`) accepted at call sites. */
type CaptureContext = {
  tags?: Record<string, unknown>;
  extra?: Record<string, unknown>;
  [key: string]: unknown;
};

/**
 * Whether the Datadog browser creds are present. When false, `initDatadogBrowser`
 * and every capture helper no-op.
 */
const isConfigured = (): boolean => !!applicationId && !!clientToken && !!site;

/**
 * Flatten a capture context into the single flat object Datadog accepts,
 * hoisting `tags`/`extra` to top-level attributes. Defensive against non-object
 * hints passed through from the telemetry facade.
 */
const toDatadogContext = (context?: unknown): object | undefined => {
  if (!context || typeof context !== 'object') return undefined;
  const { tags, extra, ...rest } = context as CaptureContext;
  return { ...rest, ...(tags ?? {}), ...(extra ?? {}) };
};

/**
 * Initialize Datadog RUM + browser logs. Safe to call repeatedly (only the
 * first call initializes). No-ops in non-browser contexts and when unconfigured.
 */
export const initDatadogBrowser = (): void => {
  if (isInitialized) return;
  if (typeof window === 'undefined') return;
  if (!isConfigured()) return;

  const apiUrl = process.env.NEXT_PUBLIC_API_URL;

  datadogRum.init({
    applicationId: applicationId as string,
    clientToken: clientToken as string,
    site,
    service: DD_SERVICE,
    env: ddEnv,
    version: ddVersion,
    // Must stay at 100 to capture all logs — error + browser-log collection is
    // scoped to sampled sessions. Tune cost via sessionReplaySampleRate instead.
    sessionSampleRate: 100,
    // Percentage of sessions that also record a Session Replay: full in dev for
    // debugging, 10% elsewhere. This is the cost knob.
    sessionReplaySampleRate: ddEnv === 'dev' ? 100 : 10,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    // Link RUM resource requests to backend APM traces via trace propagation.
    ...(apiUrl ? { allowedTracingUrls: [apiUrl] } : {}),
    // Don't send the `baggage` request header (defaults to true). Trace linkage
    // works via traceparent/x-datadog-* alone; baggage would only add
    // session/user/account IDs as an extra header and expand the API's CORS
    // allow-list surface.
    propagateTraceBaggage: false,
  });

  datadogLogs.init({
    clientToken: clientToken as string,
    site,
    service: DD_SERVICE,
    env: ddEnv,
    version: ddVersion,
    // Forward reported errors to Datadog Logs.
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
  });

  isInitialized = true;
};

/** Report an exception to Datadog RUM. No-op until initialized. */
export const ddCaptureException = (
  error: unknown,
  context?: unknown
): void => {
  if (!isInitialized) return;
  datadogRum.addError(error, toDatadogContext(context));
};

/** Report a message to Datadog browser logs. No-op until initialized. */
export const ddCaptureMessage = (message: string, context?: unknown): void => {
  if (!isInitialized) return;
  datadogLogs.logger.info(message, toDatadogContext(context));
};

/** Associate the current RUM session with a user. No-op until initialized. */
export const ddSetUser = (user: {
  id?: string;
  email?: string;
  name?: string;
  [key: string]: unknown;
}): void => {
  if (!isInitialized) return;
  datadogRum.setUser(user);
};

/** Attach a global context property to all RUM events. No-op until initialized. */
export const ddSetContext = (key: string, context: unknown): void => {
  if (!isInitialized) return;
  datadogRum.setGlobalContextProperty(key, context);
};

/**
 * Record a breadcrumb-equivalent on the RUM session. RUM has no breadcrumb
 * concept, so this maps to a custom RUM action. No-op until initialized.
 */
export const ddAddBreadcrumb = (breadcrumb: {
  category?: string;
  message?: string;
  level?: string;
  data?: Record<string, unknown>;
}): void => {
  if (!isInitialized) return;
  datadogRum.addAction(
    breadcrumb.message || breadcrumb.category || 'breadcrumb',
    {
      ...breadcrumb.data,
      category: breadcrumb.category,
      level: breadcrumb.level,
    }
  );
};
